import {
  isUsingRelativeDevProxy,
  isUsingRelativeLoginDevProxy,
  resolveApiPath,
  resolveApiPathDirect,
  resolveLoginApiPath,
  resolveLoginApiPathDirect,
} from '../baseUrl';
import { withIncludeCredentials } from './apiFetch';

/** Dev-server / proxy unhealthy — try browser → API directly (may fail on TLS/CORS). */
const PROXY_GATEWAY_STATUSES = new Set([502, 503, 504]);

/** Main cloud/API routes vs login/register/refresh auth service (`/dev-login-proxy`). */
export type DevProxyRoute = 'api' | 'login';

/**
 * Login paths use the login dev proxy; register, assets, profile, and OTP use the main API proxy.
 * (Register OTP and POST /register/* live on the main API, not the login-only service.)
 */
export function devProxyRouteForApiPath(path: string): DevProxyRoute {
  const normalized = path.replace(/^\/+/, '');
  return normalized.startsWith('login/') ? 'login' : 'api';
}

/**
 * Browser cannot disable TLS verification or CORS. The “relaxed” path is **`/dev-api-proxy`** (Node + `secure: false`).
 * Set **`REACT_APP_SKIP_DIRECT_HTTPS_FALLBACK=true`** to avoid a second `fetch` to `https://…` (often fails on cert/CORS).
 */
function skipDirectHttpsFallback(): boolean {
  return process.env.REACT_APP_SKIP_DIRECT_HTTPS_FALLBACK === 'true';
}

/** `fetch` streams the body once. After a proxy attempt, rebuild common body types before retrying direct HTTPS. */
function rebuildInitBodyForRetry(init: RequestInit): RequestInit {
  const { body } = init;
  if (body === undefined || body === null) {
    return init;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const fd = new FormData();
    body.forEach((value, key) => {
      fd.append(key, value);
    });
    return { ...init, body: fd };
  }
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    return { ...init, body: new URLSearchParams(body.toString()) };
  }
  return init;
}

function formatFetchException(err: unknown): string {
  if (err instanceof Error) {
    const dom = err as DOMException;
    const code =
      typeof dom.code === 'number' && !Number.isNaN(dom.code) && dom.code !== 0
        ? ` [DOMException code: ${dom.code}]`
        : '';
    return `${err.name}: ${err.message}${code}`;
  }
  return String(err);
}

function devProxyTroubleshootingAppendix(directUrl: string, route: DevProxyRoute): string {
  if (process.env.NODE_ENV !== 'development') {
    return '';
  }
  let host = 'API host';
  try {
    host = new URL(directUrl).hostname;
  } catch {
    /* ignore */
  }
  const envHint =
    route === 'login'
      ? '  REACT_APP_LOGIN_PROXY_TARGET=http://localhost:3000'
      : '  REACT_APP_PROXY_TARGET=https://YOUR_API_ORIGIN';
  const proxyMount = route === 'login' ? '/dev-login-proxy' : '/dev-api-proxy';
  return [
    '',
    'Development troubleshooting:',
    `• Upstream: ${host}`,
    `• Keep npm start running — setupProxy.js forwards ${proxyMount} to that origin.`,
    '• If the default host is wrong or blocked, add .env.development.local with:',
    envHint,
    '  (restart npm start; must match the URL your backend actually serves).',
    `• From a terminal, test reachability: curl -vk "${directUrl.split('?')[0]}"`,
    '• VPN, corporate firewall, or cloud IP allowlists often cause “Failed to fetch” with no HTTP status.',
    '• JSON PATCH bodies over ~100 KB or multipart totals over ~500 KB often fail on the default API host (413 or “Failed to fetch”). Updates send **only changed fields**; images are resized to ~120 KB each by default.',
    '• Slow uploads: raise **DEV_PROXY_TIMEOUT_MS** (default 900000 ms — see `setupProxy.js`).',
    '• Optional: REACT_APP_SKIP_DIRECT_HTTPS_FALLBACK=true to skip the browser’s direct HTTPS retry.',
  ].join('\n');
}

function bothPathsFailedMessage(
  proxyUrl: string,
  proxyErr: unknown,
  directUrl: string,
  directErr: unknown,
  route: DevProxyRoute,
): string {
  const core = [
    'Request failed via dev proxy, then direct HTTPS.',
    `Proxy URL: ${proxyUrl}`,
    `Proxy error: ${formatFetchException(proxyErr)}`,
    `Direct URL: ${directUrl}`,
    `Direct error: ${formatFetchException(directErr)}`,
  ].join('\n');
  return core + devProxyTroubleshootingAppendix(directUrl, route);
}

function pathsForRoute(route: DevProxyRoute, apiPathTail: string): {
  proxyUrl: string;
  directUrl: string;
  useRelativeProxy: boolean;
} {
  if (route === 'login') {
    return {
      proxyUrl: resolveLoginApiPath(apiPathTail),
      directUrl: resolveLoginApiPathDirect(apiPathTail),
      useRelativeProxy: isUsingRelativeLoginDevProxy(),
    };
  }
  return {
    proxyUrl: resolveApiPath(apiPathTail),
    directUrl: resolveApiPathDirect(apiPathTail),
    useRelativeProxy: isUsingRelativeDevProxy(),
  };
}

export type FetchWithDevProxyFallbackResult = {
  response: Response;
  /** Actual URL `fetch` used (proxy or direct HTTPS). */
  urlUsed: string;
};

/**
 * In development with `/dev-api-proxy` or `/dev-login-proxy`, tries the proxy first. If the request **throws**
 * or returns **502 / 503 / 504**, retries once with the direct origin for that route.
 */
export async function fetchWithDevProxyFallback(
  apiPathTail: string,
  init: RequestInit,
  route: DevProxyRoute = 'api',
): Promise<FetchWithDevProxyFallbackResult> {
  const { proxyUrl, directUrl, useRelativeProxy } = pathsForRoute(route, apiPathTail);
  const fetchInit = withIncludeCredentials(init);

  if (!useRelativeProxy) {
    const response = await fetch(proxyUrl, fetchInit);
    return { response, urlUsed: proxyUrl };
  }

  const noDirect = skipDirectHttpsFallback();

  try {
    const response = await fetch(proxyUrl, fetchInit);
    if (!noDirect && PROXY_GATEWAY_STATUSES.has(response.status)) {
      try {
        const directRes = await fetch(directUrl, rebuildInitBodyForRetry(fetchInit));
        return { response: directRes, urlUsed: directUrl };
      } catch (directErr) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[fetchWithDevProxyFallback] Direct retry failed after proxy HTTP',
            response.status,
            response.statusText,
            '— keeping proxy response. Compare errors:\n',
            `Proxy: HTTP ${response.status} ${response.statusText} (${proxyUrl})\n`,
            `Direct: ${formatFetchException(directErr)} (${directUrl})`,
          );
        }
        return { response, urlUsed: proxyUrl };
      }
    }
    return { response, urlUsed: proxyUrl };
  } catch (proxyErr) {
    if (noDirect) {
      throw proxyErr;
    }
    try {
      const response = await fetch(directUrl, rebuildInitBodyForRetry(fetchInit));
      return { response, urlUsed: directUrl };
    } catch (directErr) {
      throw new Error(bothPathsFailedMessage(proxyUrl, proxyErr, directUrl, directErr, route), {
        cause: proxyErr,
      });
    }
  }
}
